// =============================================================================
// Aggregation Config Component
// =============================================================================

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings,
  Plus,
  Trash2,
  Play,
  Edit,
  X,
  AlertTriangle,
  Check,
  Clock,
  Calendar,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import type {
  App,
  AggregationConfig,
  AggregationConfigInput,
  AggregationFilters,
  AggregationMetric,
  AggregationResult,
  TimeBucket,
} from '../lib/api.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Select } from './ui/select.js';
import { AggregationResultsViewer } from './AggregationResultsViewer.js';

// =============================================================================
// Types
// =============================================================================

export interface AggregationConfigProps {
  /** Available apps */
  apps: App[];
  /** Selected app ID */
  selectedAppId: string | null;
  /** Callback when app is selected */
  onSelectApp: (appId: string | null) => void;
  /** Aggregation configs for the selected app */
  configs: AggregationConfig[];
  /** Whether configs are loading */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
  /** Callback to create a config */
  onCreate: (appId: string, config: AggregationConfigInput) => Promise<void>;
  /** Callback to update a config */
  onUpdate: (appId: string, configId: string, config: Partial<AggregationConfigInput>) => Promise<void>;
  /** Callback to delete a config */
  onDelete: (appId: string, configId: string) => Promise<void>;
  /** Callback to compute aggregation */
  onCompute: (appId: string, configId: string, from: string, to: string, filters?: AggregationFilters | null) => Promise<AggregationResult[]>;
}

// =============================================================================
// Constants
// =============================================================================

const TIME_BUCKETS: Array<{ value: TimeBucket; label: string }> = [
  { value: '1m', label: '1 minute' },
  { value: '5m', label: '5 minutes' },
  { value: '15m', label: '15 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '1d', label: '1 day' },
];

const AGG_TYPES: Array<{ value: AggregationMetric['agg']; label: string }> = [
  { value: 'count', label: 'Count' },
  { value: 'count_distinct', label: 'Count Distinct' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'p50', label: 'P50' },
  { value: 'p95', label: 'P95' },
  { value: 'p99', label: 'P99' },
  { value: 'error_count', label: 'Error Count' },
  { value: 'error_rate', label: 'Error Rate %' },
];

const LOG_TYPES = ['log', 'event', 'timing', 'metric', 'http', 'job'];

// =============================================================================
// Config Form Component
// =============================================================================

interface ConfigFormProps {
  config?: AggregationConfig | null;
  onSave: (config: AggregationConfigInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

function ConfigForm({ config, onSave, onCancel, isLoading }: ConfigFormProps) {
  const [name, setName] = React.useState(config?.name || '');
  const [subAppPattern, setSubAppPattern] = React.useState(config?.sub_app_pattern || '');
  const [typePattern, setTypePattern] = React.useState(config?.type_pattern || '');
  const [messagePattern, setMessagePattern] = React.useState(config?.message_pattern || '');
  const [groupBy, setGroupBy] = React.useState<string[]>(config?.group_by || []);
  const [metrics, setMetrics] = React.useState<AggregationMetric[]>(
    config?.metrics || [{ field: '*', agg: 'count', alias: 'total' }]
  );
  const [timeBucket, setTimeBucket] = React.useState<TimeBucket>(config?.time_bucket || '1h');
  const [enabled, setEnabled] = React.useState(config?.enabled ?? true);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      await onSave({
        name: name.trim(),
        sub_app_pattern: subAppPattern.trim() || undefined,
        type_pattern: typePattern.trim() || undefined,
        message_pattern: messagePattern.trim() || undefined,
        group_by: groupBy.filter((g) => g.trim()),
        metrics: metrics.filter((m) => m.field.trim() && m.alias.trim()),
        time_bucket: timeBucket,
        enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    }
  };

  const addGroupBy = () => setGroupBy([...groupBy, '']);
  const removeGroupBy = (index: number) => setGroupBy(groupBy.filter((_, i) => i !== index));
  const updateGroupBy = (index: number, value: string) => {
    const newGroupBy = [...groupBy];
    newGroupBy[index] = value;
    setGroupBy(newGroupBy);
  };

  const addMetric = () => setMetrics([...metrics, { field: '', agg: 'count', alias: '' }]);
  const removeMetric = (index: number) => setMetrics(metrics.filter((_, i) => i !== index));
  const updateMetric = (index: number, updates: Partial<AggregationMetric>) => {
    const newMetrics = [...metrics];
    const current = newMetrics[index] ?? { field: '', agg: 'count' as const, alias: '' };
    newMetrics[index] = { ...current, ...updates };
    setMetrics(newMetrics);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-400">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Name *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., checkout_metrics"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Time Bucket</label>
          <Select
            value={timeBucket}
            onChange={(e) => setTimeBucket(e.target.value as TimeBucket)}
            options={TIME_BUCKETS}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Sub-App Pattern</label>
          <Input
            value={subAppPattern}
            onChange={(e) => setSubAppPattern(e.target.value)}
            placeholder="e.g., api or * for all"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Type Pattern</label>
          <Select
            value={typePattern}
            onChange={(e) => setTypePattern(e.target.value)}
            options={[
              { value: '', label: 'All types' },
              ...LOG_TYPES.map((t) => ({ value: t, label: t })),
            ]}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Message Pattern (regex)</label>
          <Input
            value={messagePattern}
            onChange={(e) => setMessagePattern(e.target.value)}
            placeholder="e.g., checkout\\..*"
          />
        </div>
      </div>

      {/* Group By */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Group By (context fields)</label>
          <Button type="button" variant="outline" size="sm" onClick={addGroupBy}>
            <Plus className="h-3 w-3" />
            Add Field
          </Button>
        </div>
        {groupBy.length > 0 ? (
          <div className="space-y-2">
            {groupBy.map((field, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={field}
                  onChange={(e) => updateGroupBy(index, e.target.value)}
                  placeholder="e.g., context.userId"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeGroupBy(index)}
                >
                  <X className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            No grouping configured. Results will be aggregated across all logs.
          </p>
        )}
      </div>

      {/* Metrics */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[var(--color-foreground)]">Metrics</label>
          <Button type="button" variant="outline" size="sm" onClick={addMetric}>
            <Plus className="h-3 w-3" />
            Add Metric
          </Button>
        </div>
        {metrics.length > 0 ? (
          <div className="space-y-2">
            {metrics.map((metric, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={metric.field}
                  onChange={(e) => updateMetric(index, { field: e.target.value })}
                  placeholder="Field (e.g., context.duration or *)"
                  className="flex-1"
                />
                <Select
                  value={metric.agg}
                  onChange={(e) => updateMetric(index, { agg: e.target.value as AggregationMetric['agg'] })}
                  options={AGG_TYPES}
                  className="w-[120px]"
                />
                <Input
                  value={metric.alias}
                  onChange={(e) => updateMetric(index, { alias: e.target.value })}
                  placeholder="Alias"
                  className="w-[150px]"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMetric(index)}
                >
                  <X className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            No metrics configured. Add at least one metric.
          </p>
        )}
      </div>

      {/* Enabled */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="enabled"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-[var(--color-border)] accent-[var(--color-primary)]"
        />
        <label htmlFor="enabled" className="cursor-pointer text-sm font-medium text-[var(--color-foreground)]">
          Enabled
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : config ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// =============================================================================
// Time Range Presets
// =============================================================================

const TIME_RANGE_PRESETS = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

function getTimeRangeFromPreset(preset: string): { from: Date; to: Date } {
  const now = new Date();
  const to = now;
  let from: Date;

  switch (preset) {
    case '1h':
      from = new Date(now.getTime() - 1 * 60 * 60 * 1000);
      break;
    case '6h':
      from = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      break;
    case '24h':
      from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '7d':
      from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    default:
      from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }

  return { from, to };
}

// =============================================================================
// Compute State Type
// =============================================================================

interface ComputeState {
  config: AggregationConfig;
  results: AggregationResult[];
  timeRange: { from: string; to: string };
  filters?: AggregationFilters | null;
}

// =============================================================================
// Main Component
// =============================================================================

export function AggregationConfigManager({
  apps,
  selectedAppId,
  onSelectApp,
  configs,
  isLoading = false,
  error,
  onCreate,
  onUpdate,
  onDelete,
  onCompute,
}: AggregationConfigProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = React.useState(false);
  const [editingConfig, setEditingConfig] = React.useState<AggregationConfig | null>(null);
  const [computeState, setComputeState] = React.useState<ComputeState | null>(null);
  const [computingConfigId, setComputingConfigId] = React.useState<string | null>(null);
  const [formLoading, setFormLoading] = React.useState(false);

  // Get URL params
  const viewConfigId = searchParams.get('view');
  const urlTimeRange = searchParams.get('range');
  const selectedTimeRange = urlTimeRange || '24h';

  // Track if we've done initial load from URL
  const initialLoadDone = React.useRef(false);

  // Helper to compute from URL state
  const handleComputeFromUrl = React.useCallback(async (config: AggregationConfig, timeRange: string, filters?: AggregationFilters | null) => {
    if (!selectedAppId) return;
    setComputingConfigId(config.id);
    try {
      const { from, to } = getTimeRangeFromPreset(timeRange);
      const results = await onCompute(selectedAppId, config.id, from.toISOString(), to.toISOString(), filters);
      setComputeState({
        config,
        results,
        timeRange: { from: from.toISOString(), to: to.toISOString() },
        filters,
      });
    } finally {
      setComputingConfigId(null);
    }
  }, [selectedAppId, onCompute]);

  // Load view state from URL on mount and when configs change
  React.useEffect(() => {
    if (!selectedAppId || configs.length === 0 || initialLoadDone.current) return;

    if (viewConfigId) {
      const config = configs.find((c) => c.id === viewConfigId);
      if (config) {
        initialLoadDone.current = true;
        handleComputeFromUrl(config, selectedTimeRange);
      }
    }
  }, [selectedAppId, configs, viewConfigId, selectedTimeRange, handleComputeFromUrl]);

  // Watch for URL changes - close view when view param is removed
  React.useEffect(() => {
    if (!viewConfigId && computeState) {
      setComputeState(null);
    } else if (viewConfigId && computeState && viewConfigId !== computeState.config.id) {
      // Different config in URL, need to compute it
      const config = configs.find((c) => c.id === viewConfigId);
      if (config && selectedAppId) {
        handleComputeFromUrl(config, selectedTimeRange);
      }
    }
  }, [viewConfigId, computeState, configs, selectedAppId, selectedTimeRange, handleComputeFromUrl]);

  const handleCreate = async (config: AggregationConfigInput) => {
    if (!selectedAppId) return;
    setFormLoading(true);
    try {
      await onCreate(selectedAppId, config);
      setShowForm(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (config: AggregationConfigInput) => {
    if (!selectedAppId || !editingConfig) return;
    setFormLoading(true);
    try {
      await onUpdate(selectedAppId, editingConfig.id, config);
      setEditingConfig(null);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!selectedAppId) return;
    if (!confirm('Are you sure you want to delete this aggregation config?')) return;
    await onDelete(selectedAppId, configId);
  };

  const handleCompute = async (config: AggregationConfig, filters?: AggregationFilters | null) => {
    if (!selectedAppId) return;
    setComputingConfigId(config.id);
    try {
      const { from, to } = getTimeRangeFromPreset(selectedTimeRange);
      const results = await onCompute(selectedAppId, config.id, from.toISOString(), to.toISOString(), filters);
      setComputeState({
        config,
        results,
        timeRange: { from: from.toISOString(), to: to.toISOString() },
        filters,
      });
      // Update URL with view state
      setSearchParams({ view: config.id, range: selectedTimeRange });
    } finally {
      setComputingConfigId(null);
    }
  };

  // Handle filter changes from the viewer
  const handleApplyFilters = async (filters: AggregationFilters | null) => {
    if (!computeState) return;
    await handleCompute(computeState.config, filters);
  };

  const handleCloseResults = () => {
    setComputeState(null);
    // Remove view param from URL
    setSearchParams((prev) => {
      prev.delete('view');
      return prev;
    });
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-[var(--color-muted-foreground)]">App:</label>
          <Select
            className="w-[200px]"
            value={selectedAppId || ''}
            onChange={(e) => onSelectApp(e.target.value || null)}
            options={apps.map((app) => ({ value: app.id, label: app.name }))}
            placeholder="Select an app"
          />
        </div>

        {selectedAppId && !showForm && !editingConfig && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" />
            New Aggregation
          </Button>
        )}
      </div>

      {/* No App Selected State */}
      {!selectedAppId && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-secondary)]">
              <Settings className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--color-foreground)]">
              Select an App
            </h3>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Choose an application to manage aggregation configs
            </p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-500/50 bg-red-500/10">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </CardContent>
        </Card>
      )}

      {/* Compute Results Viewer */}
      {computeState && (
        <Card>
          <CardContent className="pt-6">
            <AggregationResultsViewer
              results={computeState.results}
              config={computeState.config}
              timeRange={computeState.timeRange}
              appliedFilters={computeState.filters}
              onClose={handleCloseResults}
              onApplyFilters={handleApplyFilters}
              isComputing={computingConfigId === computeState.config.id}
            />
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Form */}
      {selectedAppId && (showForm || editingConfig) && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
                <Settings className="h-5 w-5 text-[var(--color-primary)]" />
              </div>
              <div>
                <CardTitle className="text-[var(--color-foreground)]">
                  {editingConfig ? 'Edit Aggregation' : 'New Aggregation'}
                </CardTitle>
                <CardDescription>
                  Configure how logs should be aggregated for analytics
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ConfigForm
              config={editingConfig}
              onSave={editingConfig ? handleUpdate : handleCreate}
              onCancel={() => {
                setShowForm(false);
                setEditingConfig(null);
              }}
              isLoading={formLoading}
            />
          </CardContent>
        </Card>
      )}

      {/* Configs List */}
      {selectedAppId && !showForm && !editingConfig && !computeState && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                  <Settings className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-[var(--color-foreground)]">Aggregation Configs</CardTitle>
                  <CardDescription>
                    {configs.length} configuration{configs.length !== 1 ? 's' : ''} defined
                  </CardDescription>
                </div>
              </div>
              {/* Time Range Selector */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                <Select
                  value={selectedTimeRange}
                  onChange={(e) => setSearchParams((prev) => {
                    prev.set('range', e.target.value);
                    return prev;
                  })}
                  options={TIME_RANGE_PRESETS}
                  className="w-[160px]"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-primary)] border-t-transparent" />
              </div>
            ) : configs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Settings className="h-12 w-12 text-[var(--color-muted-foreground)]/50" />
                <p className="mt-4 text-sm text-[var(--color-muted-foreground)]">
                  No aggregation configs yet. Create one to start analyzing your logs.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--color-border)]">
                {configs.map((config) => (
                  <div
                    key={config.id}
                    className={cn(
                      'flex items-center justify-between px-6 py-4',
                      'transition-colors duration-150',
                      'hover:bg-[var(--color-muted)]/20'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg',
                          config.enabled
                            ? 'bg-green-500/10 text-green-400'
                            : 'bg-[var(--color-secondary)] text-[var(--color-muted-foreground)]'
                        )}
                      >
                        {config.enabled ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                      </div>
                      <div>
                        <p className="font-medium text-[var(--color-foreground)]">{config.name}</p>
                        <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                          {config.sub_app_pattern && <span>sub_app: {config.sub_app_pattern}</span>}
                          {config.type_pattern && <span>type: {config.type_pattern}</span>}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {config.time_bucket}
                          </span>
                          {config.metrics && config.metrics.length > 0 && (
                            <span>metrics: {config.metrics.map(m => m.alias).join(', ')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCompute(config)}
                        disabled={computingConfigId === config.id}
                        title={`Run aggregation for ${TIME_RANGE_PRESETS.find(p => p.value === selectedTimeRange)?.label || 'selected time range'}`}
                      >
                        {computingConfigId === config.id ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-primary)] border-t-transparent" />
                        ) : (
                          <Play className="h-4 w-4 text-green-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingConfig(config)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(config.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AggregationConfigManager;
